import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Form, Select, Input, Button, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { getSuppliersByType } from '../../../core/api/settings';
import { createCost } from '../../../core/api/mediaplan';

/**
 * Component for displaying and managing fixed costs form
 * @param {Object} props - Component props
 * @param {boolean} props.selectedBrand - Selected brand ID
 * @param {Object} props.form - Form instance
 * @param {Function} props.onCostAdded - Callback function when a cost is added
 */
const FixedCostsForm = ({ 
  selectedBrand,
  form,
  onCostAdded
}) => {
  const [lettershopSuppliers, setLettershopSuppliers] = useState([]);
  const [postageSuppliers, setPostageSuppliers] = useState([]);
  const [dataProcessingSuppliers, setDataProcessingSuppliers] = useState([]);

  useEffect(() => {
    if (selectedBrand) {
      fetchSuppliers('Lettershop', selectedBrand);
      fetchSuppliers('Postage', selectedBrand);
      fetchSuppliers('Data Processing', selectedBrand);
    }
  }, [selectedBrand]);

  const fetchSuppliers = async (type, brandId) => {
    if (!brandId) return;
    
    try {
      const response = await getSuppliersByType(type, brandId);
      const suppliers = response.data.map(supplier => ({
        label: supplier.name,
        value: supplier.id
      }));

      switch (type) {
        case 'Lettershop':
          setLettershopSuppliers(suppliers);
          break;
        case 'Postage':
          setPostageSuppliers(suppliers);
          break;
        case 'Data Processing':
          setDataProcessingSuppliers(suppliers);
          break;
        default:
          break;
      }
    } catch (error) {
      console.error(`Error fetching ${type} suppliers:`, error);
      message.error(`Failed to fetch ${type} suppliers`);
    }
  };

  const handleAddCost = async (values, costType) => {
    if (!selectedBrand) {
      message.warning('Please select a brand first');
      return;
    }

    const payload = {
      brand: selectedBrand,
      cost_type: costType,
      supplier: values[`${costType}_supplier`],
      cost_per_mailing: parseFloat(values[`${costType}_cost`])
    };

    try {
      await createCost(payload);
      message.success(`${costType.charAt(0).toUpperCase() + costType.slice(1)} cost added successfully`);
      form.resetFields([`${costType}_supplier`, `${costType}_cost`]);
      if (onCostAdded) {
        onCostAdded();
      }
    } catch (error) {
      console.error(`Error adding ${costType} cost:`, error);
      message.error(`Failed to add ${costType} cost`);
    }
  };
  return (
    <Card title="Fixed Costs" size="small" className="mb-4" style={{ marginBottom: '16px' }}>
      <Row gutter={12}>
        <Col span={24}>
          <Row gutter={12} align="middle">
            <Col span={4}>
              <span>Lettershop:</span>
            </Col>
            <Col span={8}>
              <Form.Item
                name="lettershop_supplier"
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: '8px' }}
              >
                <Select
                  placeholder="Select provider"
                  options={lettershopSuppliers}
                  style={{ width: '100%' }}
                  disabled={!selectedBrand}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="lettershop_cost"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[0-9.]+$/, message: 'Invalid number' }
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Input
                  prefix="$"
                  placeholder="0.0000"
                  disabled={!selectedBrand}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.validateFields(['lettershop_supplier', 'lettershop_cost'])
                      .then(values => handleAddCost(values, 'lettershop'))
                      .catch(info => console.error('Validate Failed:', info));
                  }}
                  disabled={!selectedBrand}
                  size="small"
                >
                  Add
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12} align="middle">
            <Col span={4}>
              <span>Postage:</span>
            </Col>
            <Col span={8}>
              <Form.Item
                name="postage_supplier"
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: '8px' }}
              >
                <Select
                  placeholder="Select supplier"
                  options={postageSuppliers}
                  style={{ width: '100%' }}
                  disabled={!selectedBrand}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="postage_cost"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[0-9.]+$/, message: 'Invalid number' }
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Input
                  prefix="$"
                  placeholder="0.0000"
                  disabled={!selectedBrand}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.validateFields(['postage_supplier', 'postage_cost'])
                      .then(values => handleAddCost(values, 'postage'))
                      .catch(info => console.error('Validate Failed:', info));
                  }}
                  disabled={!selectedBrand}
                  size="small"
                >
                  Add
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12} align="middle">
            <Col span={4}>
              <span>Data Processing:</span>
            </Col>
            <Col span={8}>
              <Form.Item
                name="data_processing_supplier"
                rules={[{ required: true, message: 'Required' }]}
                style={{ marginBottom: '8px' }}
              >
                <Select
                  placeholder="Select supplier"
                  options={dataProcessingSuppliers}
                  style={{ width: '100%' }}
                  disabled={!selectedBrand}
                  size="small"
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                name="data_processing_cost"
                rules={[
                  { required: true, message: 'Required' },
                  { pattern: /^[0-9.]+$/, message: 'Invalid number' }
                ]}
                style={{ marginBottom: '8px' }}
              >
                <Input
                  prefix="$"
                  placeholder="0.0000"
                  disabled={!selectedBrand}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item style={{ marginBottom: '8px' }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.validateFields(['data_processing_supplier', 'data_processing_cost'])
                      .then(values => handleAddCost(values, 'data_processing'))
                      .catch(info => console.error('Validate Failed:', info));
                  }}
                  disabled={!selectedBrand}
                  size="small"
                >
                  Add
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default FixedCostsForm;
